import { isPathComplete } from './isWin.js';
import { solveLevel } from './solver.js';
import { type ColorId, type GameState, type Level, type Pos, posEqual } from './types.js';

/** 'nudge' = which pair to work on (no cells revealed), 'reveal' = a bounded stretch of that pair's remaining path. */
export type HintKind = 'nudge' | 'reveal';

export interface HintResult {
  color: ColorId;
  /** Unplayed solution cells beyond the player's current path, in order toward the target. */
  cells: Pos[];
  /**
   * True when this color's own drawn path has strayed from the level's one solution —
   * it can't be extended into a win until the player undoes the wrong stretch first.
   */
  conflict?: boolean;
}

function isPrefix(path: Pos[], solutionPath: Pos[]): boolean {
  if (path.length > solutionPath.length) return false;
  return path.every((p, i) => posEqual(p, solutionPath[i]));
}

/**
 * Picks an unsolved color whose player-drawn path (if any) is a genuine
 * prefix of the level's unique solution, then reveals a bounded stretch of
 * the remaining solution cells for that color. Never reveals the final cell
 * alone when more than one remains, so a hint never finishes a color by
 * itself — the player still has to trace it in.
 */
export function computeHint(level: Level, state: GameState, kind: HintKind): HintResult | null {
  const result = solveLevel(level.size, level.pairs, 1, level.walls ?? []);
  if (!result.solvable || !result.solution) return null;

  let best: { color: ColorId; orientedSolution: Pos[]; playerPath: Pos[] } | null = null;
  let conflict: ColorId | null = null;

  for (const [color, solutionPath] of result.solution) {
    const endpoints = state.endpoints.get(color);
    // solveLevel can occasionally return a path that stops one cell short of
    // its declared endpoint (a known solver quirk — see solver.ts). Only
    // trust paths that genuinely connect both endpoints, so a hint never
    // points somewhere wrong.
    if (!endpoints || !isPathComplete(solutionPath, endpoints)) continue;

    const playerPath = state.paths.get(color) ?? [];

    if (playerPath.length >= solutionPath.length && isPrefix(playerPath, solutionPath)) {
      continue;
    }

    const forward = playerPath.length === 0 || posEqual(playerPath[0], solutionPath[0]);
    const orientedSolution = forward ? solutionPath : [...solutionPath].reverse();
    if (!isPrefix(playerPath, orientedSolution)) {
      // The player's own line for this color doesn't lead to the level's one
      // solution — no hint can extend it. Remember the first such color so
      // we can tell the player to undo it instead of answering with silence.
      if (playerPath.length > 0 && conflict === null) conflict = color;
      continue;
    }

    if (!best || playerPath.length > best.playerPath.length) {
      best = { color, orientedSolution, playerPath };
    }
  }

  if (!best) {
    if (conflict) return { color: conflict, cells: [], conflict: true };
    return null;
  }

  const remaining = best.orientedSolution.slice(best.playerPath.length);
  if (remaining.length === 0) return null;

  if (kind === 'nudge') {
    return { color: best.color, cells: [] };
  }

  const revealCount =
    remaining.length <= 2
      ? remaining.length
      : Math.min(remaining.length - 1, Math.max(3, Math.ceil(remaining.length / 2)));
  return { color: best.color, cells: remaining.slice(0, revealCount) };
}
